/**
 * What each MCP setting means, in the operator's words.
 *
 * docs/products/mcp.md › "Panels: what the operator reads". The Settings page
 * used to title each field with its SSM path and state its timing as the API's
 * raw `effect` string; an operator needs to know what the setting does, not
 * where it is stored. The copy lives HERE, keyed by SSM path, and a key this
 * table does not know still renders — under its raw path, with its raw
 * `effect` — so a new MCP setting shows up less helpfully instead of not at all.
 */

export type SettingCard = "Messages" | "Safety" | "Audit" | "Responses" | "Other";

/** The order the cards render in. */
export const SETTING_CARDS: readonly SettingCard[] = [
  "Messages",
  "Safety",
  "Audit",
  "Responses",
  "Other",
];

export interface SettingCopy {
  card: Exclude<SettingCard, "Other">;
  label: string;
  description: string;
}

export const SETTING_COPY: Record<string, SettingCopy> = {
  "blocklist/blocked_message": {
    card: "Messages",
    label: "Blocked account",
    description: "Shown when a blocked merchant or user calls any tool. Plain text, no placeholders.",
  },
  "usage/blocked_message": {
    card: "Messages",
    label: "Tool not in the plan",
    description:
      "Shown when the merchant's plan does not include a tool (its cap is 0). It must say this is not temporary, so the assistant does not retry. Keep {tool} and {tier}.",
  },
  "usage/upsell_message": {
    card: "Messages",
    label: "Daily limit reached for a tool",
    description:
      "Shown when the merchant has used today's calls for one tool. It promises a reset at 00:00 UTC and points to the other tools. Keep {tool}, {tier} and {reset_in}.",
  },
  "usage/total_message": {
    card: "Messages",
    label: "Daily limit reached for the plan",
    description:
      "Shown when the merchant has used today's calls across all tools. Keep {tier}, {cap} and {reset_in}.",
  },
  "availability/paused_message": {
    card: "Messages",
    label: "Tool paused",
    description:
      "Shown when an assistant calls a tool paused on the Paused tools panel. It must say the tool is temporarily down and not a plan limit. Keep {tool}.",
  },
  // Its own card: it is not a refusal message, and grouping it with them
  // invites a casual edit.
  "output_guard/untrusted_framing": {
    card: "Safety",
    label: "Untrusted-data warning",
    description:
      "Put in front of every tool answer: tells the assistant to treat merchant data as data, never as instructions. A security control; weakening it weakens every call.",
  },
  "audit/gate_rows_enabled": {
    card: "Audit",
    label: "Record refused calls",
    description:
      "When on, every refused call (blocked, paused, rate-limited, over the plan) writes a row to the tool audit. Turn off only to cut write volume in an emergency.",
  },
  "response/max_chars": {
    card: "Responses",
    label: "Largest answer",
    description:
      "The most characters of merchant data one tool answer may carry; a larger answer is trimmed with a note telling the assistant to narrow the request. 1,000 to 2,000,000.",
  },
};

/** The card a key renders in. An unknown `*_message` is still a message. */
export function settingCardOf(key: string): SettingCard {
  const known = SETTING_COPY[key];
  if (known) return known.card;
  return key.endsWith("_message") ? "Messages" : "Other";
}

/** The field's title: the plain-words label, or the raw path for an unknown key. */
export function settingLabelOf(key: string): string {
  return SETTING_COPY[key]?.label ?? key;
}

const LIVE = /^live within ~(\d+)\s*s\b/;
const STAGED = "staged until POST /admin/settings/apply";

/**
 * `effect` in plain words. Matches the `live within ~` PREFIX and reads the
 * number, so a changed TTL still renders correctly; anything unrecognised is
 * shown raw rather than guessed at.
 */
export function settingEffectText(effect: unknown): string {
  if (typeof effect !== "string") return "";
  const live = LIVE.exec(effect);
  if (live) {
    const seconds = Number(live[1]);
    if (seconds < 60) return `Takes effect within about ${seconds} seconds. No restart.`;
    const minutes = Math.round(seconds / 60);
    return `Takes effect within about ${minutes} minute${minutes === 1 ? "" : "s"}. No restart.`;
  }
  if (effect.startsWith(STAGED)) {
    return "Saved now. Merchants see it only after Apply changes (a rolling restart of the MCP service) or the next deploy.";
  }
  if (effect === "read-only") return "Read-only. Changed only by an MCP deploy.";
  return effect;
}

/** Whether a saved value waits for Apply changes before merchants see it. */
export function isStagedEffect(effect: unknown): boolean {
  return typeof effect === "string" && effect.startsWith(STAGED);
}
